// Auth Store
import { getField } from 'vuex-map-fields'

const state = {
  user: null,
  addresses: [],
  pending: false,
  newsletter: null,
  countryPhoneCodes: [],
  countryPhoneCodesLoadedAt: 0,
  errors: {
    invoice_address: {},
    delivery_address: {},
  },
}

const COUNTRY_PHONE_CODES_CACHE_TTL = 24 * 60 * 60 * 1000

const getters = {
  getField,
  isNewsletterSubscribed: state => Boolean(state.newsletter?.default?.subscribed),
}

const actions = {
  getUser({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', true)

      this.api.items.getItem('user').then(({ data }) => {
        commit('SET_USER', data)
        commit('SET_PENDING', false)
        resolve()
      })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data.errors)
        })
    })
  },
  getAddresses({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', true)

      this.api.items.getItem('addresses').then(({ data }) => {
        commit('SET_ADDRESSES', data.results)
        commit('SET_PENDING', false)
        resolve()
      })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data.errors)
        })
    })
  },
  createAddress({ commit }, data) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', true)

      this.api.items.createItem('addresses', data)
        .then(({ data }) => {
          commit('SET_PENDING', false)
          resolve(data)
        })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data)
        })
    })
  },
  updateAddress({ commit }, { id, data }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', true)

      this.api.items.editItem(`addresses.${id}`, data).then(({ data }) => {
        commit('SET_PENDING', false)
        resolve(data)
      })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data)
        })
    })
  },
  deleteAddress({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', true)

      this.api.items.deleteItem(`addresses.${id}`).then(() => {
        commit('SET_PENDING', false)
        resolve()
      })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data.errors)
        })
    })
  },
  getCountryPhoneCodes({ commit }) {
    return new Promise((resolve, reject) => {
      const isCacheValid =
        state.countryPhoneCodes?.length &&
        Date.now() - state.countryPhoneCodesLoadedAt < COUNTRY_PHONE_CODES_CACHE_TTL

      if (isCacheValid) {
        return Promise.resolve(state.countryPhoneCodes)
      }
      commit('SET_PENDING', true)

      this.api.items.getItem('addresses.country_phone_codes').then(({ data }) => {
        commit('SET_COUNTRY_PHONE_CODES', data)
        commit('SET_PENDING', false)
        resolve()
      })
        .catch(({ data }) => {
          commit('SET_PENDING', false)
          reject(data.errors)
        })
    })
  },
  getNewsletter({ commit }) {
    return new Promise((resolve, reject) => {
      this.api.items.getItem('user.newsletter').then(({ data }) => {
        commit('SET_NEWSLETTER', data)
        resolve(data)
      })
        .catch(err => {
          reject(err)
        })
    })
  },
}

const mutations = {
  SET_USER: (state, payload) => {
    state.user = payload
  },
  SET_ADDRESSES: (state, payload) => {
    state.addresses = payload
  },
  SET_COUNTRY_PHONE_CODES: (state, payload) => {
    state.countryPhoneCodes = payload
    state.countryPhoneCodesLoadedAt = Date.now()
  },
  SET_NEWSLETTER: (state, payload) => {
    state.newsletter = payload
  },
  SET_PENDING: (state, payload) => {
    state.pending = payload
  },
  SET_ERRORS(state, { type, errors }) {
    // Replaced with a new object reference to ensure reactivity triggers
    state.errors = {
      ...state.errors,
      [type]: errors || {},
    }
  },
  CLEAR_FIELD_ERROR(state, { type, field }) {
    if (state.errors[type] && state.errors[type][field]) {
      // Create a shallow copy to prevent direct mutation and fix Vue reactivity
      const updatedErrors = { ...state.errors[type] }
      delete updatedErrors[field]

      state.errors = {
        ...state.errors,
        [type]: updatedErrors,
      }
    }
  },
  RESET_ERRORS(state, type) {
    state.errors = {
      ...state.errors,
      [type]: {},
    }
  },
  RESET_ALL_ERRORS(state) {
    // Fixed key mismatch: changed from billing/shipping to match state definition
    state.errors = {
      invoice_address: {},
      delivery_address: {},
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
